import {
  RepaymentPlanReportData,
  RepaymentPlanReportRow,
} from '../models/Credit report.model';

/** One repayment term's (e.g. Conventional / Islamic) part of the Repayment Plan. */
export interface RepaymentPlanTypeGroup {
  /** Repayment term label; '' when the rows carry no type. */
  type: string;
  data: RepaymentPlanReportData;
  /** Opening Exposure is proposal-level, so only the first table shows it. */
  showOpeningExposure: boolean;
}

function roundAmount(value: number): number {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function rowTotal(row: RepaymentPlanReportRow): number {
  return Object.values(row.schedule ?? {}).reduce((total, value) => {
    const amount = Number(value ?? 0);
    return Number.isFinite(amount) ? total + amount : total;
  }, 0);
}

/**
 * Splits the Repayment Plan into one schedule per repayment term type, so
 * Islamic and Conventional financing each get their own table. Groups keep
 * the order in which each type first appears; each group only carries the
 * period columns its own rows use.
 */
export function splitRepaymentPlanByType(
  data: RepaymentPlanReportData,
): RepaymentPlanTypeGroup[] {
  const rowsByType = new Map<string, RepaymentPlanReportRow[]>();

  data.rows.forEach((row) => {
    const type = String(row.type ?? '').trim();
    const rows = rowsByType.get(type) ?? [];
    rows.push(row);
    rowsByType.set(type, rows);
  });

  return Array.from(rowsByType.entries()).map(([type, rows], index) => {
    const usedPeriods = new Set<string>();
    rows.forEach((row) =>
      Object.keys(row.schedule ?? {}).forEach((period) => usedPeriods.add(period)),
    );

    return {
      type,
      data: {
        columns: data.columns.filter((column) => usedPeriods.has(column)),
        rows,
        totalDisbursementAmount: roundAmount(
          rows.reduce((total, row) => total + (Number(row.trancheAmount) || 0), 0),
        ),
        grandTotal: roundAmount(
          rows.reduce((total, row) => total + rowTotal(row), 0),
        ),
        openingExposure: data.openingExposure,
      },
      showOpeningExposure: index === 0,
    };
  });
}
